import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";

import { ASSESSMENT_CONTENTS } from "../../../constants/assessment-content";
import Dropdown from "../../../atoms/Dropdown";
import ButtonsSwitch from "../../../atoms/ButtonsSwitch";
import {
  assessmentPath,
  assessmentByClassPath,
  assessmentByGradePath,
  API_URL,
} from "../../../teacherStore";
import {
  IClass,
  IAssessmentStudent,
  IAssessmentTeacher,
  IAssessment,
  IAssessmentAnswer,
  IClassScores,
  IReferenceScores,
} from "../../../state";
import Spinner from "../../../atoms/Spinner";
import useDebounceCallback from "../../../hooks/useDebounceCallback";
import DownloadButton from "../../../atoms/DownloadButton";
import ModalConfirm from "../../../molecules/ModalConfirm";
import AssessmentStudentSummary from "../../../molecules/teacher/AssessmentStudentSummary";
import ModalAssessmentStudentDetail from "../ModalAssessmentStudentDetail";
import { ISimpleStudent } from "../../../pages/teacher/AssessmentListPage";
import AssessmentListSummaryScore from "../../../molecules/teacher/AssessmentListSummaryScore";

import styles from "./AssessmentList.module.scss";

const ORDER_SELECT_OPTIONS = [
  { value: 0, label: "生徒登録順" },
  { value: 1, label: "スコアの高い順" },
  { value: 2, label: "スコアの低い順" },
  { value: 3, label: "前回差の高い順" },
  { value: 4, label: "前回差の低い順" },
  { value: 5, label: "まとめ入力記入完了順" },
  { value: 6, label: "記入日の新しい順" },
];

const getScore = (
  student: IAssessmentStudent,
  selectedExperienceNo: number,
) => {
  const value =
    selectedExperienceNo === 0
      ? student.avg_score
      : student.assessment_experiences?.find(
          a => a.experience_no === selectedExperienceNo,
        )?.score;
  return value ?? null;
};

const getDifference = (
  student: IAssessmentStudent,
  selectedExperienceNo: number,
) => {
  const value =
    selectedExperienceNo === 0
      ? student.avg_difference
      : student.assessment_experiences?.find(
          a => a.experience_no === selectedExperienceNo,
        )?.difference;
  return value ?? null;
};

export type Props = {
  calling: boolean;
  classIDFromSearchParams: number;
  class_id: number;
  classes: IClass[];
  assessmentStudents: IAssessmentStudent[];
  assessment_answers: IAssessmentAnswer[];
  pdfToken: string | null;
  currentStudent: ISimpleStudent;
  assessment: IAssessmentTeacher;
  assessments_past: IAssessment[];
  referenceScores: {
    classScores: IClassScores | null;
    gradeScores: IReferenceScores | null;
    nationalScores: IReferenceScores | null;
    isExistAssessmentReport: boolean;
    lastUpdatedAt: Date;
  };
  update: (args: { [key: string]: any }) => void;
  fetchAssessmentStudents: (class_id: number) => void;
  handleSubmitComment: (feedback_comment: string) => void;
  onDownloadCSV: (class_id: number) => void;
  onDownloadPDF: () => void;
  onUpdateAssessmentsReport: (class_id: number) => void;
  updateCurrentStudent: (student: ISimpleStudent) => void;
  onClearPDFToken: () => void;
};

const AssessmentList: React.FC<Props> = ({
  calling,
  classIDFromSearchParams,
  class_id,
  classes,
  assessmentStudents,
  pdfToken,
  currentStudent,
  assessments_past,
  assessment,
  assessment_answers,
  referenceScores,
  update,
  fetchAssessmentStudents,
  updateCurrentStudent,
  handleSubmitComment,
  onUpdateAssessmentsReport,
  onDownloadCSV,
  onDownloadPDF,
  onClearPDFToken,
}) => {
  const [selectedOrderValue, setSelectedOrderValue] = React.useState(0);
  const [selectedExperienceNo, setSelectedExperienceNo] = React.useState(0);
  const history = useHistory();

  const selectedClass = useMemo(() => {
    if (classIDFromSearchParams) {
      const classFromSearchParams = classes.find(
        cl => cl.id === classIDFromSearchParams,
      );
      return classFromSearchParams
        ? {
            value: classFromSearchParams.id,
            label: classFromSearchParams.full_name,
          }
        : undefined;
    }
    return undefined;
  }, [classes, classIDFromSearchParams]);

  const selectedOrderOption = React.useMemo(
    () => ORDER_SELECT_OPTIONS.find(o => o.value === selectedOrderValue),
    [selectedOrderValue],
  );

  const experienceNoSelectOptions = React.useMemo(() => {
    return [
      { value: 0, label: "平均スコア" },
      ...ASSESSMENT_CONTENTS.map(c => ({
        value: c.id,
        label: c.questionTitle,
      })),
    ];
  }, []);

  const selectedExperienceNoOption = React.useMemo(
    () => experienceNoSelectOptions.find(o => o.value === selectedExperienceNo),
    [experienceNoSelectOptions, selectedExperienceNo],
  );

  const sortedAssessmentStudents = React.useMemo(() => {
    return [...assessmentStudents].sort((student1, student2) => {
      switch (selectedOrderValue) {
        case 1: // score desc
        case 2: // score asc
          const score1 = getScore(student1, selectedExperienceNo);
          const score2 = getScore(student2, selectedExperienceNo);
          if (score1 === null) return 1;
          if (score2 === null) return -1;
          return selectedOrderValue === 1 ? score2 - score1 : score1 - score2;

        case 3: // difference desc
        case 4: // difference asc
          const dif1 = getDifference(student1, selectedExperienceNo);
          const dif2 = getDifference(student2, selectedExperienceNo);
          if (dif1 === null) return 1;
          if (dif2 === null) return -1;
          return selectedOrderValue === 3 ? dif2 - dif1 : dif1 - dif2;

        case 5: // has_next_actions
          if (!student1.has_next_actions) return 1;
          if (!student2.has_next_actions) return -1;
          return student2.has_next_actions - student1.has_next_actions;

        case 6: // created_at desc
          if (student1.assessment_created_at === null) return 1;
          if (student2.assessment_created_at === null) return -1;
          return (
            new Date(student2.assessment_created_at).getTime() -
            new Date(student1.assessment_created_at).getTime()
          );

        default:
          // student_id asc
          return 0;
      }
    });
  }, [assessmentStudents, selectedOrderValue, selectedExperienceNo]);

  const students = React.useMemo(() => {
    return sortedAssessmentStudents.map(as => ({
      id: as.id,
      full_name: as.full_name,
    }));
  }, [sortedAssessmentStudents]);

  const [debounced] = useDebounceCallback(class_id => {
    fetchAssessmentStudents(class_id);
  });

  const [downloadPDFDebounced] = useDebounceCallback(() => onDownloadPDF());

  useEffect(() => {
    class_id && debounced(class_id);
  }, [debounced, class_id]);

  // 24時間以内にサマリーデータを更新している場合は「レポートデータ更新」ボタンを非活性にする
  const disableUpdateAssessmentReport = React.useMemo(() => {
    if (!referenceScores.isExistAssessmentReport) return false;
    const now = new Date().getTime();
    const lastUpdatedDate = new Date(referenceScores.lastUpdatedAt).getTime();
    const hoursDifference = (now - lastUpdatedDate) / (1000 * 60 * 60);
    return hoursDifference < 24;
  }, [referenceScores]);

  return (
    <div className={styles.wrapper}>
      {!!currentStudent.id && (
        <ModalAssessmentStudentDetail
          calling={calling}
          pdfToken={pdfToken}
          students={students}
          class_id={class_id}
          assessment={assessment}
          assessments_past={assessments_past}
          assessment_answers={assessment_answers}
          currentStudent={currentStudent}
          updateCurrentStudent={updateCurrentStudent}
          handleSubmitComment={handleSubmitComment}
          onDownloadPDF={onDownloadPDF}
          onClearPDFToken={onClearPDFToken}
          onClose={() => updateCurrentStudent({ id: 0, full_name: "" })}
        />
      )}
      <div className={styles.topContent}>
        <Dropdown
          className={styles.classSelect}
          defaultValue={selectedClass}
          options={classes.map(cl => ({
            value: cl.id,
            label: cl.full_name,
          }))}
          onChange={class_id => {
            update({ class_id });
            history.push({
              search: `?class_id=${class_id}`,
            });
          }}
        />
        <ButtonsSwitch
          buttonsData={[
            { content: "個人", path: assessmentPath },
            {
              content: "クラス集計",
              path: `${assessmentByClassPath}?class_id=${class_id}`,
            },
            {
              content: "学年集計",
              path: `${assessmentByGradePath}?grade=${
                classes.find(cl => cl.id === class_id)?.grade
              }`,
            },
          ]}
        />
      </div>
      <div className={styles.downloadWrapper}>
        <AssessmentListSummaryScore
          referenceScores={referenceScores}
          selectedExperienceNo={selectedExperienceNo}
        />
        {!!assessmentStudents.length && (
          <div className="d-flex align-items-start gap-4">
            <DownloadButton
              disabled={calling || disableUpdateAssessmentReport}
              onClick={() => onUpdateAssessmentsReport(class_id)}
            >
              {referenceScores.isExistAssessmentReport
                ? "レポートデータ更新"
                : "レポートデータ作成"}
            </DownloadButton>
            <DownloadButton
              disabled={calling || !referenceScores.isExistAssessmentReport}
              onClick={downloadPDFDebounced}
            >
              PDF一括ダウンロード
            </DownloadButton>
            <DownloadButton
              disabled={calling || !referenceScores.isExistAssessmentReport}
              onClick={() => {
                onDownloadCSV(class_id);
              }}
            >
              CSVデータをダウンロード
            </DownloadButton>
          </div>
        )}
      </div>
      {!!assessmentStudents.length && (
        <div className={styles.annotation}>
          {referenceScores.isExistAssessmentReport
            ? `${
                disableUpdateAssessmentReport
                  ? ``
                  : `ファイルのデータが古い場合は「レポートデータ更新」をクリックしてください。`
              } 最終更新日: ${format(
                new Date(referenceScores.lastUpdatedAt),
                "yyyy/MM/dd",
              )}`
            : `※ファイルをダウンロードをするには、レポートデータ作成をクリックしてください。`}
        </div>
      )}
      <div className={styles.OrderWrapper}>
        <Dropdown
          className={styles.orderSelect}
          defaultValue={selectedOrderOption}
          options={ORDER_SELECT_OPTIONS}
          onChange={setSelectedOrderValue}
        />
        <Dropdown
          className={styles.experienceNoSelect}
          defaultValue={selectedExperienceNoOption}
          options={experienceNoSelectOptions}
          onChange={setSelectedExperienceNo}
        />
      </div>
      <div className={styles.studentListWrapper}>
        {calling && <Spinner className={styles.loading} />}
        <div className={styles.studentList}>
          {sortedAssessmentStudents.length > 0 ? (
            sortedAssessmentStudents.map(s => {
              return (
                <AssessmentStudentSummary
                  key={s.id}
                  student={s}
                  selectedExperienceNo={selectedExperienceNo}
                  onClick={() => {
                    updateCurrentStudent({
                      id: s.id,
                      full_name: s.full_name,
                    });
                  }}
                />
              );
            })
          ) : (
            <p className={styles.emptyData}>データがありません。</p>
          )}
        </div>
      </div>
      {pdfToken && !currentStudent.id && (
        <ModalConfirm
          onClose={onClearPDFToken}
          title="ダウンロードしますか？"
          buttons={[
            {
              title: "キャンセル",
              action: onClearPDFToken,
              type: "None",
            },
            {
              title: "ダウンロード",
              action: () => {
                window.open(
                  `${API_URL}/assessments/download_all_student_pdf?class_id=${class_id}&temporary_token=${pdfToken}`,
                  "_blank",
                );
                onClearPDFToken();
              },
              type: "Success",
            },
          ]}
        />
      )}
    </div>
  );
};

export default AssessmentList;
